import { useNavigate } from "react-router-dom";
import { Cloud, Loader2 } from "lucide-react";
import { CHECK_MODES, type CheckMode } from "@mycompbox/shared";
import { ApiError } from "@/lib/api/client";
import { useToast } from "@/hooks/useToast";
import { useCloudRunStatus, useStartCloudRun } from "@/hooks/queries/use-cloud-runs";
import { Button } from "@/components/ui/button";
import { cloudHoursLeft } from "./run-with-choice";

/**
 * "Run in CompBox cloud": plan or build on a temporary CompBox machine with the
 * person's Anthropic key, ending as a pull request. Offered when there's no
 * computer, or the chosen one is offline (`emphasis`).
 */
export function CloudRunOffer({
  payload,
  emphasis,
  onStarted,
  stay = false,
}: {
  payload: unknown;
  emphasis: boolean;
  onStarted: (jobId: string) => void;
  /** Stay on this page instead of opening the run's page (the new look follows it in place). */
  stay?: boolean;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const p = (payload ?? {}) as {
    kind?: string;
    epicId?: string;
    epicRequestId?: string;
    fastMode?: unknown;
    checkMode?: unknown;
  };
  const cloudKind = p.kind === "plan" || p.kind === "orchestrate";
  const { data: status } = useCloudRunStatus(
    { ...(p.epicId ? { epicId: p.epicId } : {}), ...(p.epicRequestId ? { epicRequestId: p.epicRequestId } : {}) },
    cloudKind
  );
  const start = useStartCloudRun();

  if (!status?.available || !cloudKind) return null;
  const hoursLeft = cloudHoursLeft(status);

  async function run() {
    try {
      const job = await start.mutateAsync({
        kind: p.kind as "plan" | "orchestrate",
        ...(p.epicId ? { epicId: p.epicId } : {}),
        ...(p.epicRequestId ? { epicRequestId: p.epicRequestId } : {}),
        // ENG-430: a build keeps the Fast mode and Build checks choices from the
        // payload; only well-formed values are sent, and never for a plan.
        ...(p.kind === "orchestrate" && typeof p.fastMode === "boolean" ? { fastMode: p.fastMode } : {}),
        ...(p.kind === "orchestrate" &&
        typeof p.checkMode === "string" &&
        (CHECK_MODES as readonly string[]).includes(p.checkMode)
          ? { checkMode: p.checkMode as CheckMode }
          : {}),
      });
      onStarted(job.id);
      if (!stay) void navigate(`/mailbox/jobs/${job.id}`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Couldn't start it in CompBox cloud");
    }
  }

  return (
    <div
      className={`rounded-md border p-3 text-sm ${emphasis ? "border-primary/40 bg-primary/[0.06]" : "border-white/[0.08]"}`}
      data-testid="cloud-run-offer"
    >
      <div className="flex items-center gap-2 font-medium">
        <Cloud className="h-4 w-4 text-primary" />
        {emphasis ? "Run it in CompBox cloud instead" : "Or run it in CompBox cloud"}
      </div>
      <p className="mt-1 text-muted-foreground">
        A temporary CompBox machine {p.kind === "plan" ? "plans this" : "builds this"} on your Anthropic key and ends
        with a pull request. {Math.round(hoursLeft)} of {status.includedHours} cloud hours left this month.
      </p>
      <Button
        className="mt-3"
        size="sm"
        variant={emphasis ? "default" : "outline"}
        disabled={start.isPending || hoursLeft <= 0}
        onClick={() => void run()}
      >
        {start.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
        Run in CompBox cloud
      </Button>
    </div>
  );
}
